import Loading from '@/components/Loading';
import { AnalysisToolbar } from '@/components/Studies/Groups/Analysis/AnalysisToolbar';
import { EmptyState } from '@/components/Studies/Groups/Analysis/EmptyState';
import StickyItem from '@/components/UI/Sticky';
import { _isNil, _notNil } from '@/littledash';
import type { Preset } from '@/model/Preset.model';
import type { Study, StudyApiId } from '@/model/Study.model';
import { type FC, useEffect } from 'react';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { AnalysisConfigDefaultData, AnalysisTableResponse } from '@/model/Analysis.model.ts';
import { useAnalysis } from '@/utils/analysis/useAnalysis';
import { AnalysisTable } from '@/components/Studies/Groups/Analysis/AnalysisTable.tsx';
import * as Auth from '@/support/auth';

interface AnalysisProps {
  study: Study;
}

export const Analysis: FC<AnalysisProps> = ({ study }) => {
  const studyApiId: StudyApiId = study.api_id;
  const preset: Preset = study.settings;
  const hasAccess: boolean = Auth.isWriteUserForStudy(study);
  const { options, config, updateAnalysisDefault, updateAnalysisConfig } = useAnalysis();

  const {
    invoke: fetchConfigData,
    loading: loadingConfig,
    response: analysisConfigResponse,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/analysis/config',
    path: { studyApiId },
    invokeOnInit: false,
  });
  const {
    invoke: fetchDefaultData,
    loading: loadingDefault,
    response: defaultConfigResponse,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/analysis/config-default',
    path: { studyApiId },
    invokeOnInit: false,
  });

  useEffect(() => {
    if (_isNil(config) && _notNil(studyApiId)) {
      fetchConfigData();
      fetchDefaultData();
    }
  }, [studyApiId, config]);

  useEffect(() => {
    if (_notNil(analysisConfigResponse)) {
      if (analysisConfigResponse.type === 'success') {
        updateAnalysisConfig(analysisConfigResponse.body);
      }
    }
  }, [analysisConfigResponse, updateAnalysisConfig]);

  useEffect(() => {
    if (_notNil(defaultConfigResponse)) {
      if (defaultConfigResponse.body?.default_saved === true) {
        updateAnalysisDefault(
          defaultConfigResponse.body.analysis_config_default as AnalysisConfigDefaultData,
          defaultConfigResponse.body.analysis_table_default as AnalysisTableResponse
        );
      }
    }
  }, [defaultConfigResponse, updateAnalysisDefault]);

  if (loadingConfig || loadingDefault || _isNil(config)) {
    return (
      <div className="w-100 h-100">
        <Loading txt="Loading analysis configuration" />
      </div>
    );
  }

  return (
    <div className="pv4 h-100 ph4 bg-near-white">
      <div className="ui-card" style={{ minHeight: '60vh', maxHeight: '100%' }}>
        <StickyItem>
          <AnalysisToolbar studyApiId={studyApiId} preset={preset} hasAccess={hasAccess} />
        </StickyItem>
        <div className="h-100">
          {_notNil(options) && Number.isInteger(options?.day) ? (
            <AnalysisTable studyApiId={studyApiId} />
          ) : (
            <EmptyState studyApiId={studyApiId} />
          )}
        </div>
      </div>
    </div>
  );
};
